import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import { UserAccount, initialUserAccount } from '@/interfaces/account';
import { getInitialTransfer, Transfer, TransferFromCSV } from '@/interfaces/transfer';
import { IFile } from '@/interfaces/attachment';
import transferServices from '@/api/transferServices';

export type TransferType = 'csv' | 'internal' | 'external' | 'account';

interface InternalState {
  type: TransferType;
  step: 'details' | 'summary';
  transfers: Transfer[];
  fromAccount: UserAccount;
  toAccount: UserAccount;
  csvData: TransferFromCSV[];
  fileName: string;
  csvDataTotal: number;
  isInternalCSV: boolean;
  isEditedForm: boolean;
  fee: number;
  attachments: IFile[];
}

type InternalStateKeys = keyof InternalState;
type InternalTransferKeys = keyof Transfer;

const initialState: InternalState = {
  type: 'internal',
  transfers: [getInitialTransfer()],
  step: 'details',
  fromAccount: initialUserAccount,
  toAccount: initialUserAccount,
  csvData: [],
  fileName: '',
  csvDataTotal: 0,
  isInternalCSV: false,
  isEditedForm: false,
  fee: 0,
  attachments: [],
};

export const deleteTransfer = createAsyncThunk('transfer/delete', async (id: string, thunkAPI) => {
  try {
    const result = await transferServices.removeTransfer(id);
    return result;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.data);
  }
});

export const internalSlice = createSlice({
  name: 'newInternalSlice',
  initialState,
  reducers: {
    handleOnChange: (state, action: PayloadAction<{ key: InternalTransferKeys; value: any; index?: number }>) => {
      const index = action.payload.index ?? 0;
      const { key, value } = action.payload;
      const currentTransfer = state.transfers[index];

      switch (key) {
        case 'showExecuteDate':
        case 'sendNotify':
        case 'showCalendar':
        case 'fetchAccount':
        case 'fetchInProgress':
        case 'exchangeOn':
          if (typeof value === 'boolean') currentTransfer[key] = value;
          break;
        default:
          currentTransfer[key] = value;
          break;
      }
    },
    handleGeneral: (state, action: PayloadAction<{ key: InternalStateKeys; value: any }>) => {
      const { key, value } = action.payload;
      switch (key) {
        case 'type':
          state[key] = value;
          break;
        case 'step':
          state[key] = value;
          break;
        case 'csvData':
          state.csvData = value.data;
          state.csvDataTotal = value.total;
          state.isInternalCSV = value.isInternal;
          break;
        case 'csvDataTotal':
          state[key] = value;
          break;
        case 'fee':
          state[key] = value;
          break;
        case 'fileName':
          state[key] = value;
          break;
        case 'attachments':
          state[key] = value;
          break;
      }
    },
    pushRecipient: state => {
      const newTr = structuredClone(getInitialTransfer());
      newTr.currency.from = state.fromAccount.account.currency.alphabeticCode;
      newTr.currency.to = state.fromAccount.account.currency.alphabeticCode;
      state.transfers = [...state.transfers, newTr];
    },
    removeRecipient: (state, action) => {
      const index = action.payload;
      state.transfers.splice(index, 1);
      state.transfers = [...state.transfers];
    },
    resetTransfers: (state, action) => {
      const initTr = structuredClone(getInitialTransfer());
      const fromCurrency = state.fromAccount.account.currency.alphabeticCode;
      const toCurrency = action.payload !== 'account' ? fromCurrency : state.toAccount.account.currency.alphabeticCode;
      initTr.currency.from = fromCurrency;
      initTr.currency.to = toCurrency;
      state.transfers = [initTr];
      state.csvData = [];
      state.fileName = '';
      state.csvDataTotal = 0;
      state.isInternalCSV = false;
      state.fee = 0;
      state.attachments = [];
    },
    setFromAccount: (state, action: PayloadAction<UserAccount>) => {
      state.fromAccount = action.payload;
    },
    setToAccount: (state, action: PayloadAction<UserAccount>) => {
      state.toAccount = action.payload;
    },
    handleClearData: state => {
      const newTr = structuredClone(getInitialTransfer());
      newTr.currency.from = state.fromAccount.account.currency.alphabeticCode;
      newTr.currency.to = state.fromAccount.account.currency.alphabeticCode;
      state.transfers = [newTr];
      state.step = 'details';
      state.type = 'internal';
      state.csvData = [];
      state.fileName = '';
      state.csvDataTotal = 0;
      state.isInternalCSV = false;
      state.fee = 0;
      state.attachments = [];
    },
  },
});

export const {
  handleOnChange,
  setFromAccount,
  setToAccount,
  handleClearData,
  pushRecipient,
  handleGeneral,
  resetTransfers,
  removeRecipient,
} = internalSlice.actions;

export const selectTransfer = (state: RootState) => state.transfer;

export default internalSlice.reducer;
